import React from 'react';
import {useSelector} from 'react-redux';
import {Navigate, Route, Routes} from 'react-router-dom';


import {ProfileTabSeparator} from 'src/components/profile-tab-structure';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCOUNT_TABS} from 'src/constants';
import {ValidationContextProvider} from 'src/contexts/validation-context';
import {AccountPageTabs} from 'src/modules/profile/account/components/account-page-structure/account-page-tabs';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {CONFERENCE_SUB_TABS} from 'src/modules/profile/account/tabs/confenrence/constants';
import {AccountBilling} from 'src/modules/profile/account/tabs/license-and-features/basic-tab-scructure/sections/account-billing';
import {LicenseInformation} from 'src/modules/profile/account/tabs/license-and-features/basic-tab-scructure/sections/license-information';
import {LicenseNotes} from 'src/modules/profile/account/tabs/license-and-features/basic-tab-scructure/sections/license-notes';
import {NoLicensePage} from 'src/modules/profile/account/tabs/license-and-features/no-license-page/no-license-page';

import Loading from '@pb/kit-component-loading';

import {AdvertisementConfigurations} from './advertisement';
import {Content} from './content';
import {NoConferencePage} from './create-conference';
import {ConferenceDetails} from './details';
import {useGetConferenceId} from './useGetConferenceId';

import {conference, conferenceMain, conferenceSidebar, conferenceWrapper} from './conference.module.styl';

type Props = {
    accountId: number,
};

export const Conference = ({accountId}: Props) => {
    const {license} = useSelector(selectAccountDetails);
    const licenseId = license?.licenseId;
    const {
        isLoading: isConferenceLoading,
        loadingError: conferenceLoadingError,
        data: conferenceId,
        loadData: loadConferenceId,
    } = useGetConferenceId({accountId});

    return (
        <div className={`enable-selection ${conferenceWrapper}`}>
            <AccountPageTabs {...{accountId, activeTab: ACCOUNT_TABS.CONFERENCE.name}}/>
            <div className={conference}>
                <div className={conferenceSidebar}>
                    {licenseId ? (
                        <>
                            <ValidationContextProvider key="license-information-section">
                                <LicenseInformation licenseId={+licenseId} accountId={+accountId}/>
                            </ValidationContextProvider>
                            <ProfileTabSeparator/>
                            <ValidationContextProvider key="account-billing">
                                <AccountBilling
                                    key="account-billing-section"
                                    licenseId={+licenseId}
                                    accountId={+accountId}
                                />
                            </ValidationContextProvider>
                            <ProfileTabSeparator/>
                            <LicenseNotes key="license-notes-section" licenseId={+licenseId} accountId={+accountId}/>
                        </>
                    ) : <NoLicensePage accountId={accountId}/>}
                </div>
                <div className={conferenceMain}>
                    <Skeleton shown={isConferenceLoading} view={<Loading fast={true}/>}>
                        <ServerResponseHandler serverError={conferenceLoadingError}>
                            {!conferenceId ? <NoConferencePage accountId={accountId} onAssign={loadConferenceId}/> : (
                                <Routes>
                                    <Route
                                        path={`/${CONFERENCE_SUB_TABS.DETAILS.name}`}
                                        element={(
                                            <ConferenceDetails
                                                accountId={accountId}
                                                conferenceId={conferenceId}
                                            />
                                        )}
                                    />

                                    <Route
                                        path={`/${CONFERENCE_SUB_TABS.ADVERTISEMENT.name}`}
                                        element={(
                                            <AdvertisementConfigurations
                                                accountId={accountId}
                                                conferenceId={conferenceId}
                                            />
                                        )}
                                    />

                                    <Route
                                        path={`/${CONFERENCE_SUB_TABS.CONTENT.name}`}
                                        element={(
                                            <Content
                                                accountId={accountId}
                                                conferenceId={conferenceId}
                                            />
                                        )}
                                    />

                                    <Route
                                        path="*"
                                        element={(
                                            <Navigate
                                                to={`/profile/account/${accountId}/${ACCOUNT_TABS.CONFERENCE.name}/${CONFERENCE_SUB_TABS.DETAILS.name}`}
                                            />
                                        )}
                                    />
                                </Routes>
                            )}
                        </ServerResponseHandler>
                    </Skeleton>
                </div>
            </div>
        </div>
    );
};
